/**
 * Walking a page and saying what is on it.
 *
 * One walk, two outputs. The proof is SVG because a browser draws it natively;
 * the press file is PDF because PDF can say "this ink, exactly" and SVG cannot.
 * If those two were produced by two walks they would eventually disagree, and a
 * customer would approve one thing and receive another - which is the single
 * failure this whole architecture exists to prevent.
 *
 * So the walk lives here and knows nothing about either format. It reduces a
 * page to four kinds of instruction:
 *
 *   group   position, rotation and opacity, which both formats nest
 *   path    everything with an outline: glyphs, ellipses, stars, arrows
 *   rect    the common case, kept separate because both formats have one
 *   image   a placed picture, never resized here
 *
 * Millimetres throughout, y down, origin at the top-left of the trim. Where a
 * format wants something else - PDF measures in points from the bottom - that
 * is the format's business.
 */

import type {
  Fill,
  Colour,
  Mask,
  PictureFill,
  GraphicLayer,
  GroupLayer,
  ImageLayer,
  Layer,
  Page,
  QrLayer,
  ShapeLayer,
  TextLayer,
  TrimboxDocument,
} from '../model/types';
import { isGradient } from '../model/types';
import { ptToMm } from '../model/units';
import { grid as qrGrid } from '../codes/qr';
import { shapeDef, shapePath } from '../shapes/library';
import type { Shaper } from '../text/shaper';
import { isBent, placeText, type Matrix } from '../text/place';
import { plan, type Pass } from '../effects/plan';
import type { HaloSpec, Outline } from '../effects/halo';
import { boxOutline, fades, featherMm, frameMatrix, multiply, unflip, type MaskSpec } from '../effects/mask';

export type { MaskSpec };

export interface Paint {
  /** A colour, or a gradient. Strokes stay single-coloured. */
  fill?: Fill | null;
  stroke?: Colour | null;
  /** Stroke width in mm. */
  width?: number;
  dash?: number[];
  /**
   * The box a gradient runs across, when there is one.
   *
   * SVG can say "the bounding box of the thing being filled" and leave it to
   * the renderer; PDF cannot - a shading is painted in real coordinates. So the
   * walk, which knows the layer's size, says it outright rather than each sink
   * measuring the path and arriving at slightly different answers.
   */
  box?: { x: number; y: number; w: number; h: number };
  /**
   * How opaque, from 0 to 1. Absent means solid.
   *
   * Only a shadow uses this. Everything else takes its opacity from the layer's
   * own group, which is one setting for the whole object rather than a
   * different one per path.
   */
  alpha?: number;
  /**
   * How corners of a stroke are drawn. Round on letters and on any outline an
   * effect adds - a thick mitred outline spikes at every sharp corner of a
   * glyph - and the formats' own default, mitred, everywhere else, so a
   * stroked rectangle keeps square corners.
   */
  join?: 'round';
}

export interface Placed {
  x: number;
  y: number;
  w: number;
  h: number;
  fit: ImageLayer['fit'];
  crop: ImageLayer['crop'];
  filters: ImageLayer['filters'];
  /**
   * Which part of the source lands in the box, as proportions of the source.
   *
   * The box shows exactly this rectangle and nothing else, so a sink places the
   * picture and clips - it does no fitting of its own. Values may fall outside
   * 0 to 1, which is what a letterbox is: 'contain' asks for more than the
   * picture has, and the surplus is empty.
   *
   * null when the source size is not known, and then the sink fits as best it
   * can and any crop is lost.
   */
  source: { x: number; y: number; w: number; h: number } | null;
}

export interface Sink {
  /** Opens a layer's own frame. Every drawing inside is relative to it. */
  group( info: {
    id: string;
    name: string;
    x: number;
    y: number;
    w: number;
    h: number;
    rotation: number;
    opacity: number;
    /** Mirrored about the layer's own middle. */
    flipX: boolean;
    flipY: boolean;
  } ): void;
  endGroup(): void;

  /**
   * An outline, as SVG path data in millimetres.
   *
   * SVG path syntax because that is what a font hands back and what this
   * project already reads; a format that cannot use it directly converts.
   */
  path( d: string, paint: Paint ): void;

  rect( x: number, y: number, w: number, h: number, radius: number, paint: Paint ): void;

  image( ref: string, placed: Placed ): void;
  /** True when this sink needs a picture it can load, not a path on disk. */
  wantsInline?: boolean;

  /**
   * A soft-edged copy of an outline: a glow, a neon, a blurred shadow.
   *
   * Handed over as the outline and the softness rather than as pixels, because
   * the two formats want different things. SVG can blur for itself, exactly;
   * PDF cannot blur at all, so the PDF sink rasterises it (effects/halo.ts) into
   * an image of the one ink with the softness in its mask.
   */
  halo( spec: HaloSpec & { colour: Colour } ): void;

  /**
   * Everything drawn until endMask is seen through an outline.
   *
   * Hard-edged it is a clip, in vectors, in both files. With a soft edge or a
   * fade it is transparency: SVG masks it, PDF carries a soft mask rasterised
   * at press resolution (effects/mask.ts). Masks may nest.
   */
  beginMask( spec: MaskSpec ): void;
  endMask(): void;
}

export interface Resolved {
  /** Where an asset's file is, however the caller found it. */
  file( assetId: string ): string | null;
  /**
   * The same picture in a form a browser can load, for the SVG.
   *
   * A press file is read by the worker, which can open a path on disk. An SVG
   * proof is looked at in a browser, which cannot: a path written into an
   * `<image href>` there is a blank where the customer's logo should be, on the
   * basket, at the checkout, and in "please check your design". It went
   * unnoticed for a while because the PDF beside it was perfectly correct.
   *
   * Optional: without it the SVG falls back to the path, which is right for a
   * caller that will read the file itself.
   */
  inline?( assetId: string ): string | null;
  /**
   * How big an asset is, in pixels.
   *
   * Asked for here rather than inside a sink because which part of a picture
   * shows is decided by the crop and the fit together, and that sum has to be
   * done once. The canvas, the proof and the press file showing different parts
   * of the same photograph is precisely the failure this shared walk exists to
   * prevent.
   *
   * Optional: without it the sinks fall back to fitting the picture themselves,
   * which is right for everything except a crop.
   */
  size?( assetId: string ): { w: number; h: number } | null;
  /** A shaper for a face, or null when the font is missing. */
  shaper( family: string, weight: number, style: 'normal' | 'italic' ): Shaper | null;
}

export interface Missing {
  fonts: string[];
  assets: string[];
}

/**
 * Walks one page.
 *
 * @returns What could not be found, which preflight has usually already said.
 */
export function drawPage( page: Page, resolve: Resolved, sink: Sink ): Missing {
  const fonts = new Set<string>();
  const assets = new Set<string>();

  const layers = () => {
    for ( const layer of page.layers ) {
      drawLayer( layer, resolve, sink, fonts, assets );
    }
  };

  // The band round a canvas, underneath the picture it is taken from.
  edgeBand( page, sink, layers );
  layers();

  return { fonts: Array.from( fonts ), assets: Array.from( assets ) };
}

/**
 * A multi-panel canvas, cut into the sheets a press actually prints.
 *
 * The customer designs a triptych as one wide picture, because that is what it
 * is on the wall. The press prints three canvases. So the page is sliced here,
 * before anything else looks at it: each panel becomes an ordinary page of its
 * own, carrying the whole artwork shifted into place.
 *
 * It has to be done here rather than inside a sink, and that is not a
 * preference. A panel's edge band is mirrored about that panel's own edges, and
 * two neighbouring panels' bands occupy exactly the same millimetres of the
 * original page with different content in them. One page cannot hold both. Cut
 * first, and each panel is a page like any other - band, bleed, folds and all -
 * with nothing downstream needing to know that panels exist.
 *
 * Everything outside a panel is simply off its sheet, which is what bleed
 * already is.
 */
export function splitPanels( doc: TrimboxDocument ): TrimboxDocument {
  if ( ! doc.pages.some( ( page ) => ( page.panels ?? 1 ) > 1 ) ) return doc;

  const pages: Page[] = [];

  for ( const page of doc.pages ) {
    const count = page.panels ?? 1;

    if ( count < 2 ) {
      pages.push( page );
      continue;
    }

    const single = page.size.w / count;

    for ( let panel = 0; panel < count; panel++ ) {
      pages.push( {
        ...page,
        id: `${ page.id }-${ panel + 1 }`,
        name: `${ page.name } ${ panel + 1 }`,
        size: { w: single, h: page.size.h },
        panels: 1,
        /*
         * Shifted as one group rather than by moving every layer: a group is
         * already a frame both sinks know how to place, and rewriting each
         * layer's position would have to understand nesting, rotation and
         * flips to get the same answer.
         */
        layers: [ {
          id: `panel-${ panel + 1 }`,
          type: 'group',
          name: 'Panel',
          x: -single * panel,
          y: 0,
          w: page.size.w,
          h: page.size.h,
          rotation: 0,
          opacity: 1,
          locked: false,
          hidden: false,
          children: page.layers,
        } ],
      } );
    }
  }

  return { ...doc, pages };
}

/**
 * The sides of a canvas, when they are not part of the page.
 *
 * A colour edge is four rectangles. A mirrored edge is the picture itself,
 * reflected about each edge and clipped to the band - so the side carries on
 * from the face and a photograph's sky stays sky round the corner.
 *
 * The reflections are drawn by walking the page again inside a mirrored frame
 * rather than by copying pixels, which keeps the sides as sharp as the face and
 * keeps both press formats honest: each sink already knows how to mirror a
 * group and how to clip to an outline, and neither has to learn anything new.
 *
 * It does mean the artwork is walked nine times for a mirrored edge. That is
 * the cost of vector sides, and it is paid on a file the customer never waits
 * for.
 */
function edgeBand( page: Page, sink: Sink, layers: () => void ): void {
  const edge = page.edge;

  if ( ! edge || ! ( edge.depth > 0 ) ) return;

  const { w, h } = page.size;
  const d = edge.depth;

  if ( 'colour' === edge.mode ) {
    const paint: Paint = { fill: { space: 'rgb', value: edge.colour } };

    sink.rect( -d, -d, w + d * 2, d, 0, paint );
    sink.rect( -d, h, w + d * 2, d, 0, paint );
    sink.rect( -d, 0, d, h, 0, paint );
    sink.rect( w, 0, d, h, 0, paint );

    return;
  }

  /*
   * Where each reflected copy sits, and which band keeps it.
   *
   * A group is mirrored about its own middle, so a copy placed one full width
   * to the left and flipped is the picture reflected about its left edge -
   * which is what a mirror wrap is.
   */
  const copies: Array<{ x: number; y: number; flipX: boolean; flipY: boolean; band: [ number, number, number, number ] }> = [
    { x: -w, y: 0, flipX: true, flipY: false, band: [ -d, 0, d, h ] },
    { x: w, y: 0, flipX: true, flipY: false, band: [ w, 0, d, h ] },
    { x: 0, y: -h, flipX: false, flipY: true, band: [ 0, -d, w, d ] },
    { x: 0, y: h, flipX: false, flipY: true, band: [ 0, h, w, d ] },
    { x: -w, y: -h, flipX: true, flipY: true, band: [ -d, -d, d, d ] },
    { x: w, y: -h, flipX: true, flipY: true, band: [ w, -d, d, d ] },
    { x: -w, y: h, flipX: true, flipY: true, band: [ -d, h, d, d ] },
    { x: w, y: h, flipX: true, flipY: true, band: [ w, h, d, d ] },
  ];

  for ( const copy of copies ) {
    const [ bx, by, bw, bh ] = copy.band;

    sink.beginMask( {
      outlines: [ { d: rectPath( bx, by, bw, bh ), m: IDENTITY } ],
      box: { x: bx, y: by, w: bw, h: bh },
      feather: 0,
      fade: null,
    } );

    sink.group( {
      id: `edge-${ bx }-${ by }`,
      name: 'Edge',
      x: copy.x,
      y: copy.y,
      w,
      h,
      rotation: 0,
      opacity: 1,
      flipX: copy.flipX,
      flipY: copy.flipY,
    } );

    layers();

    sink.endGroup();
    sink.endMask();
  }
}

/** The page's own background, if it has one. Drawn across the whole sheet. */
export function pageBackground( page: Page ): Colour | null {
  return page.background?.type === 'colour' ? page.background.value : null;
}

function drawLayer(
  layer: Layer,
  resolve: Resolved,
  sink: Sink,
  fonts: Set<string>,
  assets: Set<string>
): void {
  if ( layer.hidden ) return;

  // A placeholder that reached the press is a fault, not a shape to draw.
  // Preflight blocks on it; here it simply is not output.
  if ( layer.type === 'placeholder' || layer.type === 'barcode' || layer.type === 'svg' ) return;

  sink.group( {
    id: layer.id,
    name: layer.name,
    x: layer.x,
    y: layer.y,
    w: layer.w,
    h: layer.h,
    rotation: layer.rotation,
    opacity: layer.opacity,
    flipX: !! layer.flipX,
    flipY: !! layer.flipY,
  } );

  switch ( layer.type ) {
    case 'text':
      drawText( layer, resolve, sink, fonts, assets );
      break;
    case 'shape':
      drawShape( layer, sink );
      break;
    case 'graphic':
      drawGraphic( layer, sink );
      break;
    case 'qr':
      drawQr( layer, sink );
      break;
    case 'image':
      drawImage( layer, resolve, sink, assets, fonts );
      break;
    case 'group':
      // A group's children carry positions on the page, and the group has been
      // opened at its own corner - so each child is drawn at its offset.
      for ( const child of ( layer as GroupLayer ).children ) {
        drawLayer( shifted( child, -layer.x, -layer.y ), resolve, sink, fonts, assets );
      }
      break;
    default:
      break;
  }

  sink.endGroup();
}

function shifted( layer: Layer, dx: number, dy: number ): Layer {
  return { ...layer, x: layer.x + dx, y: layer.y + dy };
}

/**
 * Text, as outlines.
 *
 * The same layout the canvas used, from the same shaper. A difference here
 * would be a card that looks right on screen and wrong on paper.
 */
function drawText( layer: TextLayer, resolve: Resolved, sink: Sink, missing: Set<string>, assets?: Set<string> ): void {
  const placed = layText( layer, resolve, missing );

  if ( ! placed ) return;

  const outlines: Outline[] = placed.glyphs.map( ( g ) => ( { d: g.d, m: g.m } ) );
  const box = { x: 0, y: 0, w: layer.w, h: layer.h };
  const picture = layer.picture && layer.picture.asset ? layer.picture : null;

  const passes = plan(
    layer,
    {
      // Effects that copy the letters take the picture's stand-in colour.
      fill: picture ? firstColour( layer.fill ) : layer.fill,
      stroke: layer.stroke ? layer.stroke.colour : null,
      width: layer.stroke ? layer.stroke.width : 0,
    },
    placed.extent
  );

  for ( const pass of passes ) {
    const rules = pass.body ? placed.rules : [];

    if ( pass.body && picture && pass.fill ) {
      // The letters, and their rules, are the window; the picture is behind.
      withLetters( sink, outlines, rules, pass, box, () => drawPicture( picture, box, resolve, sink, assets ?? new Set() ) );

      if ( pass.stroke ) drawPass( { ...pass, fill: null }, outlines, box, sink, undefined, true );
      continue;
    }

    if ( isGradient( pass.fill ) && pass.source === 'outline' && pass.blur <= 0 ) {
      /*
       * One ramp across the whole text, not one per letter: the letters clip a
       * rectangle of gradient. Painting each glyph with its own gradient gave
       * every letter the full ramp, and in the PDF - where a glyph has its own
       * tiny coordinate space - no visible ramp at all.
       */
      const gradient = pass.fill;

      for ( let n = 0; n < pass.repeat; n++ ) {
        withLetters( sink, outlines, rules, pass, box, () =>
          sink.rect( box.x + pass.dx, box.y + pass.dy, box.w, box.h, 0, {
            fill: gradient,
            box: { x: box.x + pass.dx, y: box.y + pass.dy, w: box.w, h: box.h },
            alpha: pass.alpha < 1 ? pass.alpha : undefined,
          } )
        );
      }

      if ( pass.stroke ) drawPass( { ...pass, fill: null }, outlines, box, sink, undefined, true );
      continue;
    }

    drawPass( pass, outlines, box, sink, undefined, true );

    // The rules belong to the letters, so they go down with the body - after
    // anything the effect put behind it. They take the fill, never a gradient:
    // a line a tenth of a millimetre thick has nowhere to put a ramp.
    if ( pass.body ) {
      const ink = pass.fill ? firstColour( pass.fill ) : pass.stroke;

      for ( const rule of placed.rules ) {
        if ( ink ) sink.rect( rule.x, rule.y, rule.w, rule.h, 0, { fill: ink } );
      }
    }
  }
}

/** Draws inside a text's letters (and its rules), moved by a pass's offset. */
function withLetters(
  sink: Sink,
  outlines: Outline[],
  rules: Array<{ x: number; y: number; w: number; h: number }>,
  pass: Pass,
  box: { x: number; y: number; w: number; h: number },
  draw: () => void
): void {
  const moved: Outline[] = outlines.map( ( o ) => ( { d: o.d, m: [ o.m[ 0 ], o.m[ 1 ], o.m[ 2 ], o.m[ 3 ], o.m[ 4 ] + pass.dx, o.m[ 5 ] + pass.dy ] } ) );

  for ( const rule of rules ) moved.push( { d: rectPath( rule.x + pass.dx, rule.y + pass.dy, rule.w, rule.h ), m: IDENTITY } );

  sink.beginMask( { outlines: moved, box, feather: 0, fade: null } );
  draw();
  sink.endMask();
}

function rectPath( x: number, y: number, w: number, h: number ): string {
  return `M ${ mm( x ) } ${ mm( y ) } L ${ mm( x + w ) } ${ mm( y ) } L ${ mm( x + w ) } ${ mm( y + h ) } L ${ mm( x ) } ${ mm( y + h ) } Z`;
}

/**
 * A text laid out and placed, as the canvas lays it.
 *
 * @returns null when the font is missing, which is recorded.
 */
function layText( layer: TextLayer, resolve: Resolved, missing: Set<string> ): ReturnType<typeof placeText> | null {
  const shaper = resolve.shaper( layer.font.family, layer.font.weight, layer.font.style );

  if ( ! shaper ) {
    missing.add( `${ layer.font.family } ${ layer.font.weight }` );

    return null;
  }

  const unitsPerMm = shaper.unitsPerEm / ptToMm( layer.font.size );

  // Bent text does not wrap - see wrapWidth in text/layout.ts, which the
  // canvas uses and which this must match.
  const wrap = isBent( layer ) ? Infinity : layer.w > 0 ? layer.w : Infinity;

  const paragraph = shaper.layout( layer.text, {
    width: wrap * unitsPerMm,
    align: layer.align,
    lineHeight: layer.lineHeight,
    letterSpacing: layer.letterSpacing * unitsPerMm,
    direction: layer.direction,
  } );

  // Straight, on an arc or along a line: the same placement the canvas draws.
  return placeText( layer, { paragraph, unitsPerMm } );
}

function drawShape( layer: ShapeLayer, sink: Sink ): void {
  const def = shapeDef( layer.shape );

  /*
   * A line has no inside, so its colour is its stroke - and a customer who has
   * set no stroke still means the fill they chose. Every other shape takes the
   * paint as it comes. A stroke is one colour, so a line "filled" with a
   * gradient takes the gradient's first stop rather than nothing at all.
   */
  const own = def.strokeOnly
    ? {
        fill: null,
        stroke: layer.stroke ? layer.stroke.colour : firstColour( layer.fill ),
        width: layer.stroke ? layer.stroke.width : 0.25,
        dash: layer.stroke?.dash,
      }
    : {
        fill: layer.fill,
        stroke: layer.stroke ? layer.stroke.colour : null,
        width: layer.stroke ? layer.stroke.width : 0,
        dash: layer.stroke?.dash,
      };

  const box = { x: 0, y: 0, w: layer.w, h: layer.h };
  const outlines: Outline[] = [ { d: shapePath( layer ), m: IDENTITY } ];

  for ( const pass of plan( layer, own, box ) ) {
    // A copy of a line is a line: whatever colour the effect gives it goes on
    // its stroke, at the line's own width.
    const drawn = def.strokeOnly && ! pass.body && pass.source === 'outline' && pass.fill && ! pass.stroke
      ? { ...pass, stroke: firstColour( pass.fill ), width: own.width, fill: null }
      : pass;

    drawPass( drawn, outlines, box, sink, def.strokeOnly ? own.width : undefined );
  }
}

const IDENTITY: Matrix = [ 1, 0, 0, 1, 0, 0 ];

/**
 * One pass of an effect, said to a sink.
 *
 * @param box The layer's box, which is what a gradient runs across.
 * @param line The object's stroke width when it is a line, so a halo of it is
 *   a halo of the stroke rather than of the nothing inside it.
 * @param letters Whether the outlines are glyphs, whose strokes are round-joined.
 */
function drawPass(
  pass: Pass,
  outlines: Outline[],
  box: Paint[ 'box' ],
  sink: Sink,
  line?: number,
  letters = false
): void {
  if ( pass.source === 'box' && pass.box ) {
    const b = pass.box;

    sink.rect( b.x + pass.dx, b.y + pass.dy, b.w, b.h, b.r, {
      fill: pass.fill,
      alpha: pass.alpha < 1 ? pass.alpha : undefined,
    } );

    return;
  }

  if ( pass.blur > 0 ) {
    const colour = pass.fill ? firstColour( pass.fill ) : pass.stroke;

    if ( colour ) {
      sink.halo( {
        outlines,
        dx: pass.dx,
        dy: pass.dy,
        blur: pass.blur,
        repeat: pass.repeat,
        alpha: pass.alpha,
        stroke: line,
        colour,
      } );
    }

    return;
  }

  const paint: Paint = {
    box,
    fill: pass.fill,
    stroke: pass.stroke,
    width: pass.width,
    dash: pass.dash,
    alpha: pass.alpha < 1 ? pass.alpha : undefined,
    join: pass.stroke && ( letters || ! pass.body ) ? 'round' : undefined,
  };

  for ( let n = 0; n < pass.repeat; n++ ) {
    for ( const outline of outlines ) {
      sink.path( placeOutline( outline, pass.dx, pass.dy ), paint );
    }
  }
}

/**
 * An outline, moved into place.
 *
 * A shape's outline is already in millimetres and is moved by rewriting it -
 * which is how a shadow has always been written, and keeps those files
 * byte-for-byte what they were. A glyph carries its matrix, and the move goes
 * on the matrix instead.
 */
function placeOutline( outline: Outline, dx: number, dy: number ): string {
  const m = outline.m;
  const plain = m[ 0 ] === 1 && m[ 1 ] === 0 && m[ 2 ] === 0 && m[ 3 ] === 1 && m[ 4 ] === 0 && m[ 5 ] === 0;

  if ( plain ) return dx || dy ? shift( outline.d, dx, dy ) : outline.d;

  return transformPath( outline.d, [ m[ 0 ], m[ 1 ], m[ 2 ], m[ 3 ], m[ 4 ] + dx, m[ 5 ] + dy ] );
}

/**
 * A QR code, as rectangles.
 *
 * The same grid the canvas drew, quiet zone included, so a code that scanned in
 * the proof scans off the press.
 */
/**
 * A graphic from the shop's library: its paths, in order, back to front.
 *
 * Each path carries its own ink, so a drawing that was three colours stays
 * three colours - and a customer who recolours it has changed the ink rather
 * than tinted a picture.
 */
function drawGraphic( layer: GraphicLayer, sink: Sink ): void {
  for ( const path of graphicPaths( layer ) ) {
    if ( ! path.fill && ! path.stroke ) continue;

    sink.path( path.d, {
      fill: path.fill,
      stroke: path.stroke,
      width: path.width,
      dash: path.dash,
      box: { x: 0, y: 0, w: layer.w, h: layer.h },
    } );
  }
}

function drawQr( layer: QrLayer, sink: Sink ): void {
  const built = qrGrid( layer );

  if ( ! built ) return;

  // A background with no ink in it is paper, and drawing paper onto paper only
  // gives the press one more thing to hold in register.
  const blank = layer.background.space === 'cmyk' && layer.background.value.every( ( c ) => c === 0 );

  if ( ! blank ) {
    sink.rect( 0, 0, layer.w, layer.h, 0, { fill: layer.background } );
  }

  const side = Math.min( layer.w, layer.h );
  const offsetX = ( layer.w - side ) / 2;
  const offsetY = ( layer.h - side ) / 2;
  const module = side / built.count;
  const paint: Paint = { fill: layer.fill };

  for ( let row = 0; row < built.count; row++ ) {
    let run = 0;

    // Runs of dark modules become one rectangle: half the drawing calls, and
    // no hairline seams between abutting fills.
    for ( let col = 0; col <= built.count; col++ ) {
      const dark = col < built.count && built.dark[ row * built.count + col ];

      if ( dark ) {
        run++;
        continue;
      }

      if ( run > 0 ) {
        sink.rect(
          offsetX + ( col - run ) * module,
          offsetY + row * module,
          run * module,
          module,
          0,
          paint
        );

        run = 0;
      }
    }
  }
}

/**
 * A picture, at whatever resolution it has. Never upscaled. §12 step 4.
 *
 * Seen through its mask, when it has one. An empty frame - a mask waiting for
 * a picture - prints nothing at all.
 */
function drawImage( layer: ImageLayer, resolve: Resolved, sink: Sink, missing: Set<string>, fonts?: Set<string> ): void {
  if ( ! layer.asset ) return;

  const file = resolve.file( layer.asset );

  if ( ! file ) {
    missing.add( layer.asset );

    return;
  }

  const masked = !! layer.mask?.layer && ! layer.mask.layer.hidden;
  const spec = layer.mask ? maskSpec( layer, layer.mask, resolve, fonts ?? new Set() ) : null;

  // A mask whose outline cannot be made - its font is missing - shows nothing
  // rather than the whole picture it was meant to cut down.
  if ( masked && ! spec ) return;

  if ( spec ) sink.beginMask( spec );

  // The sink says what it can load; the resolver says what it can offer.
  const ref = sink.wantsInline && resolve.inline ? resolve.inline( layer.asset ) ?? file : file;

  sink.image( ref, {
    x: 0,
    y: 0,
    w: layer.w,
    h: layer.h,
    fit: layer.fit,
    crop: layer.crop,
    filters: layer.filters,
    source: sourceRegion( layer, resolve.size ? resolve.size( layer.asset ) : null ),
  } );

  if ( spec ) sink.endMask();
}

/** A picture inside a text's letters, fitted to the text's box. */
function drawPicture( picture: PictureFill, box: { x: number; y: number; w: number; h: number }, resolve: Resolved, sink: Sink, missing: Set<string> ): void {
  const file = resolve.file( picture.asset );

  if ( ! file ) {
    missing.add( picture.asset );

    return;
  }

  const ref = sink.wantsInline && resolve.inline ? resolve.inline( picture.asset ) ?? file : file;
  const size = resolve.size ? resolve.size( picture.asset ) : null;

  sink.image( ref, {
    ...box,
    fit: picture.fit,
    crop: picture.crop,
    filters: { brightness: 0, contrast: 0, saturation: 0, greyscale: false },
    source: sourceRegion( { fit: picture.fit, crop: picture.crop, w: box.w, h: box.h }, size ),
  } );
}

/**
 * A picture's mask, as outlines in the picture's own millimetres.
 *
 * @returns null when there is nothing to do (a hard mask on the plain box) or
 *   the outline could not be made.
 */
export function maskSpec( layer: ImageLayer, mask: Mask, resolve: Resolved, fonts: Set<string> ): MaskSpec | null {
  const feather = featherMm( mask, layer.w, layer.h );
  const box = { x: 0, y: 0, w: layer.w, h: layer.h };
  const fade = fades( mask.fade ) ? mask.fade : null;

  if ( ! mask.layer || mask.layer.hidden ) {
    if ( feather <= 0 && ! fade ) return null;

    return { outlines: [ boxOutline( layer.w, layer.h, feather ) ], box, feather, fade };
  }

  const made = maskOutlines( mask.layer, resolve, fonts );

  if ( ! made ) return null;

  const frame = multiply( unflip( layer ), frameMatrix( mask.layer ) );

  return {
    outlines: made.outlines.map( ( o ) => ( { d: o.d, m: multiply( frame, o.m ) } ) ),
    evenOdd: made.evenOdd,
    box,
    feather,
    fade,
  };
}

/** A mask layer's outline, in that layer's own frame. */
export function maskOutlines(
  layer: NonNullable<Mask[ 'layer' ]>,
  resolve: Resolved,
  fonts: Set<string>
): { outlines: Outline[]; evenOdd: boolean } | null {
  if ( layer.type === 'shape' ) {
    return { outlines: [ { d: shapePath( layer ), m: IDENTITY } ], evenOdd: false };
  }

  if ( layer.type === 'graphic' ) {
    return {
      outlines: graphicPaths( layer ).map( ( p ) => ( { d: p.d, m: IDENTITY } ) ),
      evenOdd: layer.paths.length > 0 && layer.paths.every( ( p ) => p.evenOdd ),
    };
  }

  const placed = layText( layer, resolve, fonts );

  if ( ! placed ) return null;

  const outlines: Outline[] = placed.glyphs.map( ( g ) => ( { d: g.d, m: g.m } ) );

  for ( const rule of placed.rules ) outlines.push( { d: rectPath( rule.x, rule.y, rule.w, rule.h ), m: IDENTITY } );

  return { outlines, evenOdd: false };
}

/** The single colour that best stands for a fill, for the things that need one. */
export function firstColour( fill: Fill ): Colour {
  if ( ! isGradient( fill ) ) return fill;

  return fill.stops[ 0 ]?.colour ?? { space: 'cmyk', value: [ 0, 0, 0, 1 ] };
}

/**
 * Which part of a picture the box shows.
 *
 * This is the same sum the canvas does in ImageNode's cropFor, and it is
 * deliberately the same shape so the two can be read side by side. It lives
 * here because the proof and the press file must show what the customer was
 * looking at when they approved it - not a second opinion about how a photo
 * ought to fit.
 *
 * The crop is applied first, as proportions of the source, so changing the fit
 * afterwards keeps the crop the customer chose. Then the fit adjusts that
 * region to the box's shape:
 *
 *   fill      takes the region as it is and stretches it
 *   cover     narrows the region to the box's ratio - the long side is cut off
 *   contain   widens it past the picture's edge, and the surplus is the
 *             letterbox
 *
 * @param size Source pixels. null when it is not known.
 * @returns Proportions of the source, or null when nothing can be worked out.
 */
export function sourceRegion(
  layer: Pick<ImageLayer, 'crop' | 'fit' | 'w' | 'h'>,
  size: { w: number; h: number } | null
): { x: number; y: number; w: number; h: number } | null {
  if ( ! size || size.w <= 0 || size.h <= 0 ) return null;

  const region = {
    x: layer.crop.x * size.w,
    y: layer.crop.y * size.h,
    w: Math.max( 1, layer.crop.w * size.w ),
    h: Math.max( 1, layer.crop.h * size.h ),
  };

  if ( layer.fit !== 'fill' && layer.w > 0 && layer.h > 0 ) {
    const boxRatio = layer.w / layer.h;
    const imageRatio = region.w / region.h;
    const wider = imageRatio > boxRatio;

    if ( layer.fit === 'cover' ) {
      if ( wider ) {
        // Wider than the box: keep a full-height slice from the middle.
        const w = region.h * boxRatio;

        region.x += ( region.w - w ) / 2;
        region.w = w;
      } else {
        const h = region.w / boxRatio;

        region.y += ( region.h - h ) / 2;
        region.h = h;
      }
    } else if ( wider ) {
      const h = region.w / boxRatio;

      region.y -= ( h - region.h ) / 2;
      region.h = h;
    } else {
      const w = region.h * boxRatio;

      region.x -= ( w - region.w ) / 2;
      region.w = w;
    }
  }

  return {
    x: region.x / size.w,
    y: region.y / size.h,
    w: region.w / size.w,
    h: region.h / size.h,
  };
}

/**
 * Where the whole picture goes, so that the wanted region covers the box.
 *
 * Both sinks need this and both must agree on it, so it is worked out once from
 * numbers the walk has already settled. The sink then clips to the box, and
 * everything outside the region falls away.
 */
export function wholeImage(
  box: { x: number; y: number; w: number; h: number },
  source: { x: number; y: number; w: number; h: number }
): { x: number; y: number; w: number; h: number } {
  const w = box.w / ( source.w || 1 );
  const h = box.h / ( source.h || 1 );

  return { x: box.x - source.x * w, y: box.y - source.y * h, w, h };
}

/* ---------------------------------------------------------------
 * Geometry
 * ------------------------------------------------------------ */

export const mm = ( value: number ) => Math.round( value * 1000 ) / 1000;

/**
 * The same path, moved.
 *
 * Only the M, L and C this project produces - it is not a general path parser
 * and does not need to be, because every path it is handed comes from the shape
 * library or from a glyph.
 */
export function shift( d: string, dx: number, dy: number ): string {
  return d.replace( /([MLC])([^MLCZmlcz]*)/g, ( _all, kind: string, rest: string ) => {
    const numbers = ( rest.match( /-?[\d.]+/g ) ?? [] ).map( Number );
    const moved = numbers.map( ( n, i ) => mm( n + ( i % 2 === 0 ? dx : dy ) ) );

    return `${ kind } ${ moved.join( ' ' ) } `;
  } );
}


/**
 * Wraps path data in a transform, without touching the data itself.
 *
 * Glyph outlines are long and every format here can apply a transform, so the
 * cheap and exact thing is to hand the outline over untouched and say where it
 * goes. Rewriting the coordinates would round every one of them twice.
 */
function transformPath( d: string, m: Matrix ): string {
  return `@${ m.join( ',' ) }@${ d }`;
}

/**
 * Reads back what transformPath wrote.
 *
 * The matrix is a full affine one, because a glyph on an arc or along a line
 * is turned as well as placed. `turned` says whether it is, so a sink can keep
 * writing the plain translate-and-scale it always wrote for straight text.
 *
 * @returns The transform and the untouched path data.
 */
export function readTransform( d: string ): { m: Matrix | null; turned: boolean; scale: number; d: string } {
  if ( d[ 0 ] !== '@' ) return { m: null, turned: false, scale: 1, d };

  const end = d.indexOf( '@', 1 );
  const n = d.slice( 1, end ).split( ',' ).map( Number );
  const m: Matrix = [ n[ 0 ] ?? 1, n[ 1 ] ?? 0, n[ 2 ] ?? 0, n[ 3 ] ?? 1, n[ 4 ] ?? 0, n[ 5 ] ?? 0 ];

  return {
    m,
    turned: Math.abs( m[ 1 ] ) > 1e-12 || Math.abs( m[ 2 ] ) > 1e-12,
    // How much the matrix enlarges, for stroke widths: a width is given in
    // millimetres of the layer, and a glyph's own space is a fraction of one.
    scale: Math.sqrt( Math.abs( m[ 0 ] * m[ 3 ] - m[ 1 ] * m[ 2 ] ) ) || 1,
    d: d.slice( end + 1 ),
  };
}

/**
 * A graphic's paths, in the layer's own millimetres.
 *
 * The drawing is kept from 0 to 1 inside its box, so resizing the layer is
 * free; this is where that box becomes real. Only the numbers change - the
 * commands are the same M, L, C and Z the importer wrote.
 */
export function graphicPaths( layer: GraphicLayer ): Array<{ d: string; fill: Fill | null; stroke: Colour | null; width: number; dash: number[] }> {
  // A stroke has one width in both directions, so it is scaled by the box's
  // geometric mean - the same measure svg/import.ts divided it by.
  const across = Math.sqrt( layer.w * layer.h );

  return layer.paths.map( ( path ) => ( {
    d: scalePath( path.d, layer.w, layer.h ),
    fill: path.fill ?? null,
    stroke: path.stroke ? path.stroke.colour : null,
    width: path.stroke ? path.stroke.width * across : 0,
    dash: path.stroke ? path.stroke.dash.map( ( gap ) => gap * across ) : [],
  } ) );
}

/** Path data with every coordinate multiplied by the box it belongs to. */
export function scalePath( d: string, sx: number, sy: number ): string {
  let even = true;

  return d.replace( /-?\d*\.?\d+(?:e-?\d+)?/gi, ( number ) => {
    const scaled = Number( number ) * ( even ? sx : sy );

    even = ! even;

    return String( Math.round( scaled * 1e4 ) / 1e4 );
  } );
}

/* ---------------------------------------------------------------
 * The cut
 * ------------------------------------------------------------ */

/** The name every cutter and RIP looks for on a die line. */
export const CUT_INK = 'CutContour';

/**
 * The spot ink an eyelet ring is named for.
 *
 * Its own name rather than CutContour: a cutter following CutContour would cut
 * the ring out, and a banner with twelve 12 mm discs cut from its hem is worse
 * than one with no marks at all.
 */
export const EYELET_INK = 'TrimboxEyelet';

/**
 * The line a rounded-corner card is cut along, as SVG path data in mm.
 *
 * One definition for the press file's die line, the proof's clip and the SVG,
 * so the corner the customer approves is the corner the cutter takes. Quarter
 * circles as cubic Béziers, which is how both formats say an arc and within a
 * few hundredths of a millimetre of a true one at any card radius.
 */
export function cutPath( x: number, y: number, w: number, h: number, r: number ): string {
  const radius = Math.max( 0, Math.min( r, w / 2, h / 2 ) );
  const k = 0.5522847498307936 * radius;
  const n = ( value: number ) => Math.round( value * 1e4 ) / 1e4;

  if ( radius === 0 ) {
    return `M ${ n( x ) } ${ n( y ) } L ${ n( x + w ) } ${ n( y ) } L ${ n( x + w ) } ${ n( y + h ) } L ${ n( x ) } ${ n( y + h ) } Z`;
  }

  return [
    `M ${ n( x + radius ) } ${ n( y ) }`,
    `L ${ n( x + w - radius ) } ${ n( y ) }`,
    `C ${ n( x + w - radius + k ) } ${ n( y ) } ${ n( x + w ) } ${ n( y + radius - k ) } ${ n( x + w ) } ${ n( y + radius ) }`,
    `L ${ n( x + w ) } ${ n( y + h - radius ) }`,
    `C ${ n( x + w ) } ${ n( y + h - radius + k ) } ${ n( x + w - radius + k ) } ${ n( y + h ) } ${ n( x + w - radius ) } ${ n( y + h ) }`,
    `L ${ n( x + radius ) } ${ n( y + h ) }`,
    `C ${ n( x + radius - k ) } ${ n( y + h ) } ${ n( x ) } ${ n( y + h - radius + k ) } ${ n( x ) } ${ n( y + h - radius ) }`,
    `L ${ n( x ) } ${ n( y + radius ) }`,
    `C ${ n( x ) } ${ n( y + radius - k ) } ${ n( x + radius - k ) } ${ n( y ) } ${ n( x + radius ) } ${ n( y ) }`,
    'Z',
  ].join( ' ' );
}

/**
 * Where a wrapped canvas folds round its bars, as fold positions.
 *
 * The page is the face plus one bar depth on every side, so the four fold lines
 * are that depth in from each edge. They are given to foldMarks() rather than
 * drawn differently, because they are the same thing to a press: a line the
 * sheet is bent along, marked outside the artwork so the mark is never printed
 * on the part anybody sees.
 *
 * @param size The page, in mm - the printed size, not the face.
 * @param wrap One bar depth, in mm.
 */
export function wrapFolds(
  size: { w: number; h: number },
  wrap: number | null | undefined
): { across: number[]; down: number[] } | null {
  if ( ! wrap || wrap <= 0 || size.w <= wrap * 2 || size.h <= wrap * 2 ) return null;

  return { across: [ wrap, size.w - wrap ], down: [ wrap, size.h - wrap ] };
}

/**
 * The marks that tell a folding machine where the creases go.
 *
 * Short dashed ticks in the bleed, above and below the sheet (or left and
 * right of it), and never a line across the artwork: a fold line printed on
 * the card is a fold line the customer receives. The ticks are trimmed off
 * with the rest of the bleed, having done their job on the press.
 *
 * @param size  The trim, in mm.
 * @param folds Where it folds, in mm from the trim's top-left.
 * @param bleed How far the sheet runs past the trim, in mm.
 * @return Line segments in trim coordinates; a tick outside the trim has
 *         negative coordinates, exactly as the bleed does.
 */
/** Radius an eyelet ring is drawn at, mm. A 12 mm eyelet, which is the usual one. */
export const EYELET_MM = 6;

/**
 * Where the eyelets are, as something a press file can carry.
 *
 * Two kinds of mark, because an eyelet has to be findable without ever being
 * printed on anything the customer will see:
 *
 *   ticks  In the bleed, off each edge, exactly like fold marks. Always drawn.
 *          They are trimmed away with the rest of the bleed having done their
 *          job, so they cost nothing and cannot spoil anything.
 *   rings  The hole itself, drawn only where it falls inside a band the
 *          hardware hides - a hem, a pole pocket. That material is folded out
 *          of sight, so a ring there is an instruction to the finisher. A ring
 *          anywhere else would be ink on the finished banner, so it is not
 *          drawn: the ticks still say where the hole goes.
 *
 * @param size    The trim, in mm.
 * @param eyelets Hole centres in trim coordinates.
 * @param shade   Bands the hardware hides, in the same coordinates.
 * @param bleed   How far the sheet runs past the trim, in mm.
 */
export function eyeletMarks(
  size: { w: number; h: number },
  eyelets: Array<{ x: number; y: number }> | null | undefined,
  shade: Array<{ x: number; y: number; w: number; h: number }> | null | undefined,
  bleed: number
): {
  ticks: Array<{ x1: number; y1: number; x2: number; y2: number }>;
  rings: Array<{ x: number; y: number; r: number }>;
} {
  const ticks: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];
  const rings: Array<{ x: number; y: number; r: number }> = [];

  if ( ! eyelets || ! eyelets.length ) return { ticks, rings };

  const gap = bleed > 0 ? Math.min( bleed / 4, 0.5 ) : 0;
  const length = bleed > 0 ? Math.max( 0.5, Math.min( bleed - gap, 4 ) ) : 0;

  const hidden = ( hole: { x: number; y: number } ) =>
    ( shade ?? [] ).some(
      ( band ) =>
        hole.x >= band.x - EYELET_MM &&
        hole.x <= band.x + band.w + EYELET_MM &&
        hole.y >= band.y - EYELET_MM &&
        hole.y <= band.y + band.h + EYELET_MM
    );

  for ( const hole of eyelets ) {
    if ( hidden( hole ) ) rings.push( { x: hole.x, y: hole.y, r: EYELET_MM } );

    if ( bleed <= 0 ) continue;

    /*
     * Ticked off the nearer edge, along whichever axis the hole runs with. A
     * hole near the top is found by looking along the top; one down the left
     * edge is found by looking along the left. Ticking both would double every
     * corner and say nothing more.
     */
    const across = Math.min( hole.y, size.h - hole.y ) <= Math.min( hole.x, size.w - hole.x );

    if ( across ) {
      const top = hole.y <= size.h - hole.y;

      ticks.push(
        top
          ? { x1: hole.x, y1: -gap, x2: hole.x, y2: -gap - length }
          : { x1: hole.x, y1: size.h + gap, x2: hole.x, y2: size.h + gap + length }
      );
    } else {
      const left = hole.x <= size.w - hole.x;

      ticks.push(
        left
          ? { x1: -gap, y1: hole.y, x2: -gap - length, y2: hole.y }
          : { x1: size.w + gap, y1: hole.y, x2: size.w + gap + length, y2: hole.y }
      );
    }
  }

  return { ticks, rings };
}

export function foldMarks(
  size: { w: number; h: number },
  folds: { across: number[]; down: number[] } | null | undefined,
  bleed: number
): Array<{ x1: number; y1: number; x2: number; y2: number }> {
  if ( ! folds || bleed <= 0 ) return [];

  /*
   * A tick long enough to see, and short enough to stay on the sheet: it
   * starts a little outside the trim and stops before the edge of the bleed,
   * because anything past that is outside the page and is never printed.
   */
  const gap = Math.min( bleed / 4, 0.5 );
  const length = Math.max( 0.5, Math.min( bleed - gap, 4 ) );
  const out: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];

  for ( const at of folds.across ) {
    if ( at <= 0 || at >= size.w ) continue;

    out.push( { x1: at, y1: -gap, x2: at, y2: -gap - length } );
    out.push( { x1: at, y1: size.h + gap, x2: at, y2: size.h + gap + length } );
  }

  for ( const at of folds.down ) {
    if ( at <= 0 || at >= size.h ) continue;

    out.push( { x1: -gap, y1: at, x2: -gap - length, y2: at } );
    out.push( { x1: size.w + gap, y1: at, x2: size.w + gap + length, y2: at } );
  }

  return out;
}
